import { useEffect, useMemo, useRef, useState } from "react";
import { Carousel, Dropdown } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import iconPopular from "../assets/images/icon-popular.svg";
import logFour from "../assets/images/log_four.png";
import logOne from "../assets/images/log_one.png";
import logThree from "../assets/images/log_three.png";
import menuCricket from "../assets/images/menu-4.png";
import menuCasino from "../assets/images/menu-99998.png";
import {
  default as homeIcon,
  default as menuHome,
} from "../assets/images/menu-home.png";
import menuInplay from "../assets/images/menu-inplay.png";
import UVGamesGif from "../assets/images/casino/uv_games.gif";
import wpSupportImg from "../assets/images/casino/wp_support.png";
import { IS_SIGNUP_SHOW, isFeatureHidden } from "../b2bconfig";
import { usePwaInstall } from "../hooks/usePwaInstall";
import Dialog from "../shared/Dialog";
import { APKIcon } from "../shared/icon";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  demoLoginAsync,
  loginAsync,
  logout as logoutAction,
} from "../store/slices/authSlice";
import { fetchHomeAllMatches } from "../store/slices/matchesSlice";
import { getSportsInfoAsync } from "../store/slices/sidebarSlice";
import { fetchSearchMatchesAsync } from "../store/slices/sportSlice";
import { clearAuthData, getUserData, isAuthenticated } from "../utils/auth";
import BonusRules from "./BonusRules";
import Rules from "./Rules";
import { MENU_ITEMS } from "./menuData";
import { BRAND_NAME_WITHOUT_SUBDOMAIN } from "../env";

const HEADER_BASE_NAV_ITEMS = [
  { id: "home001", to: "/", label: "Home", iconSrc: menuHome },
  { id: "inplay001", to: "/inplay", label: "in-play", iconSrc: menuInplay },
];

const CRICKET_EVENT_TYPE_ID = 4;
const FOOTBALL_EVENT_TYPE_ID = 1;
const TENNIS_EVENT_TYPE_ID = 2;
const MAX_MATCHES_HOME = 10;

const filterLive = (matches) =>
  (matches || []).filter((m) => m.in_play === 1).slice(0, MAX_MATCHES_HOME);

const normalizeSportName = (name) => {
  const key = String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[\s\W_]+/g, "");
  if (key === "soccer") return "football";
  if (key === "sportbook") return "sportsbook";
  return key;
};

const getSportIdForMenuLabel = (label) => {
  const k = normalizeSportName(label);
  const map = {
    football: 1,
    tennis: 2,
    cricket: 4,
    rugby: 5,
    horseracing: 7,
    tabletennis: 20,
    futsal: 29,
    darts: 3503,
    kabaddi: 99994,
    binary: 99990,
    politics: 2378961,
    sportsbook: 99991,
    baseball: 7511,
    basketball: 7522,
    volleyball: 998917,
    icehockey: 7524,
    mixedmartialarts: 26420387,
    greyhoundracing: 4339,
  };
  return map[k];
};

const formatAmount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showRulesDialog, setShowRulesDialog] = useState(false);
  const [showBonusRulesDialog, setShowBonusRulesDialog] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    if (typeof window === "undefined") return false;
    return isAuthenticated();
  });

  const brandNameWithoutSubdomain = BRAND_NAME_WITHOUT_SUBDOMAIN();
  const bonusAmounts = useSelector((state) => state?.bonus?.bonusAmounts || []);

  const loginLoading = useAppSelector((state) => state.auth?.loading);
  const [userData, setUserData] = useState(() => {
    if (typeof window === "undefined") return null;
    return getUserData();
  });

  const dispatch = useAppDispatch();
  const balance = useSelector((state) => state.bet?.balance);
  const exposure = useSelector((state) => state.bet?.balance?.liability ?? 0);
  const authUser = useSelector((state) => state.auth?.user);
  const authToken = useSelector((state) => state.auth?.token);
  const sportsInfo = useSelector((state) => state.sidebar?.sportsInfo);
  const logoUrl = useAppSelector((state) => state.logo?.logoUrl);
  const logoLoading = useAppSelector((state) => state.logo?.loading);
  const langData = useAppSelector((state) => state.common?.langData);
  const { supported, isInstalled, install } = usePwaInstall();
  const canInstallPwa = supported && !isInstalled;

  // Keep header auth UI in sync when Redux is cleared (e.g. API response with logout: true)
  useEffect(() => {
    if (authToken || authUser) {
      setIsLoggedIn(true);
      setUserData(getUserData());
    } else if (!isAuthenticated()) {
      setIsLoggedIn(false);
      setUserData(null);
    }
  }, [authToken, authUser]);

  useEffect(() => {
    dispatch(getSportsInfoAsync());
  }, [dispatch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("openLogin") !== "1") return;
    if (!isLoggedIn) {
      setShowLoginDialog(true);
    }
    params.delete("openLogin");
    const next = params.toString();
    navigate(
      { pathname: location.pathname, search: next ? `?${next}` : "" },
      { replace: true },
    );
  }, [location.search, location.pathname, isLoggedIn, navigate]);
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [, setSubmitting] = useState(false);

  const authState = useSelector((state) => state.auth);
  const isDemoUser = !!authState?.user?.is_demo || !!authState?.isDemoLogin;

  const [showUvMiniCasinoBtn, setShowUvMiniCasinoBtn] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 720 : false,
  );
  const whatsappUrl = useAppSelector((state) => state.sidebar?.whatsappUrl);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 719px)");
    const sync = () => setShowUvMiniCasinoBtn(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const mlobbyBtnClick = () => {
    if (!isLoggedIn) {
      setShowLoginDialog(true);
      return;
    }
    if (isDemoUser) {
      toast.error("Demo users are not allowed to play casino games.");
      return;
    }
    document
      .getElementsByClassName("mlobby-main-dc")[0]
      ?.classList.remove("d-none");
    document
      .getElementsByClassName("mini-casino-btn")[0]
      ?.classList.add("d-none");

    const routerEl = document.getElementsByClassName("router-ctn")[0];
    if (routerEl) {
      routerEl.style.maxHeight = "calc(100% - 250px)";
      routerEl.style.overflowY = "scroll";
    }
  };

  const handleWalletRouteNavigation = (
    event,
    path,
    closeProfileMenu = false,
  ) => {
    if (event?.preventDefault) event.preventDefault();
    if (closeProfileMenu) setIsProfileMenuOpen(false);

    if (isDemoUser) {
      toast.error("please login with real user id");
      return;
    }

    navigate(path);
  };

  //inplay dynamic
  const { matchesBySport } = useSelector((state) => state.matches);

  const cricketAll = matchesBySport[String(CRICKET_EVENT_TYPE_ID)] || [];
  const footballAll = matchesBySport[String(FOOTBALL_EVENT_TYPE_ID)] || [];
  const tennisAll = matchesBySport[String(TENNIS_EVENT_TYPE_ID)] || [];

  const cricketMatches = useMemo(() => filterLive(cricketAll), [cricketAll]);
  const footballMatches = useMemo(() => filterLive(footballAll), [footballAll]);
  const tennisMatches = useMemo(() => filterLive(tennisAll), [tennisAll]);

  const showCriketFooballTenisInplayMatches = useMemo(() => {
    const all = [...cricketMatches, ...footballMatches, ...tennisMatches].slice(
      0,
      10,
    );
    return all.map((match) => {
      return {
        name: match.name,
        match_id: match.match_id,
        sport_id: match.sport_id,
      };
    });
  }, [cricketMatches, footballMatches, tennisMatches]);

  // -------- Quick Search (events) --------
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchContainerRef = useRef(null);

  const getSportIdForMatch = (m) =>
    Number(m?.sport_id ?? m?.sports_id ?? m?.event_type_id ?? m?.sportId);
  const isRacingSportId = (sportId) => sportId === 7 || sportId === 4339;

  const getLabelForMatch = (m) =>
    String(m?.name ?? m?.match_name ?? m?.series_name ?? m?.sport_name ?? "");
  const formatMatchTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${hh}:${mm}`;
    } catch {
      return "";
    }
  };

  const buildMatchRoute = (m) => {
    const matchId = m?.match_id ?? m?.event_id ?? m?.id ?? "";
    // Racing routes are intentionally excluded from quick-search suggestions.
    // If they ever slip through, keep a sane fallback route.
    return `/sports/detail/${matchId}`;
  };

  useEffect(() => {
    const q = String(searchQuery || "").trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const action = await dispatch(fetchSearchMatchesAsync());

        if (cancelled) return;

        if (fetchSearchMatchesAsync.fulfilled.match(action)) {
          const payload = action.payload;
          const responseData = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.data)
              ? payload.data
              : Array.isArray(payload?.result)
                ? payload.result
                : Array.isArray(payload?.matches)
                  ? payload.matches
                  : [];

          if (responseData?.length >= 3) {
            const matchesList = Array.isArray(responseData) ? responseData : [];
            const query = q.toLowerCase();
            const filtered = matchesList.filter((match) => {
              const haystack = [
                match?.match_name,
                match?.matchName,
                match?.eventName,
                match?.name,
                match?.competition,
                match?.seriesName,
                match?.series_name,
                match?.teamName1,
                match?.teamName2,
                match?.teamA,
                match?.teamB,
              ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
              return haystack.includes(query);
            });
            setSearchResults(filtered);
          } else {
            setSearchResults([]);
          }
        } else {
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dispatch, searchQuery]);

  const suggestions = useMemo(() => {
    const list = (Array.isArray(searchResults) ? searchResults : []).filter(
      (m) => {
        const sportId = getSportIdForMatch(m);
        return !isRacingSportId(sportId);
      },
    );
    // De-duplicate by label
    const seen = new Set();
    const unique = [];
    for (const m of list) {
      const label = getLabelForMatch(m);
      const key = `${label}-${m?.match_id ?? m?.event_id ?? m?.id ?? ""}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(m);
      }
      if (unique.length >= 10) break;
    }
    return unique;
  }, [searchResults]);

  useEffect(() => {
    const handler = (e) => {
      if (!searchOpen) return;
      const node = searchContainerRef.current;
      if (node && !node.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [searchOpen]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    if (suggestions.length > 0) {
      const first = suggestions[0];
      setSearchOpen(false);
      setSearchQuery("");
      navigate(buildMatchRoute(first));
    }
  };

  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    setSearchOpen(val.trim().length > 2);
  };

  // Expose an imperative helper so other components (e.g. detail pages)
  // can trigger the existing login modal without duplicating it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.openLoginDialog = () => {
      setShowLoginDialog(true);
    };
    return () => {
      // Clean up when header unmounts
      if (typeof window !== "undefined" && window.openLoginDialog) {
        delete window.openLoginDialog;
      }
    };
  }, []);

  const getErrorMessage = (err) => {
    if (!err) return "Login failed";
    if (typeof err === "string") return err;
    // Common backend shapes
    return (
      err?.message ||
      err?.msg ||
      err?.error ||
      err?.data?.msg ||
      err?.data?.message ||
      "Login failed"
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const u = username.trim();
    const p = password;

    if (!u || !p) {
      toast.error("Please enter both username and password.");
      return;
    }

    setSubmitting(true);
    const action = await dispatch(loginAsync({ username: u, password: p }));

    if (loginAsync.fulfilled.match(action)) {
      const result = action.payload;
      if (result?.status) {
        toast.success(result?.msg || "Logged in successfully", {
          toastId: "auth_login_success",
        });
        setShowLoginDialog(false);
        setIsLoggedIn(true);
        setUserData(getUserData());
        setUsername("");
        setPassword("");
      } else {
        toast.error(
          result?.msg ||
            result?.message ||
            getErrorMessage(result) ||
            "Login failed",
        );
      }
    } else {
      const errPayload = action.payload;
      toast.error(
        errPayload?.msg ||
          errPayload?.message ||
          getErrorMessage(errPayload) ||
          "Login failed",
      );
    }
    setSubmitting(false);
  };

  const handle2FASettings = () => {
    if (!isLoggedIn) {
      toast.error("Please login with real account to access 2FA settings");
      return;
    }
    // if (!isLoggedIn || isDemoUser) {
    //   toast.error("Please login with real account to access 2FA settings");
    //   return;
    // }

    navigate("/2fa-settings");
  };

  const toggleSidebar = () => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("toggle-sidebar");
  };

  const handleDemoLogin = async () => {
    setSubmitting(true);
    const action = await dispatch(demoLoginAsync());

    if (demoLoginAsync.fulfilled.match(action)) {
      const result = action.payload;
      toast.success(result?.msg || "Logged in successfully", {
        toastId: "auth_login_success",
      });
      setShowLoginDialog(false);
      setIsLoggedIn(true);
      setUserData(getUserData());
    } else {
      const err = action.payload;
      toast.error(
        err?.msg || err?.message || getErrorMessage(err) || "Demo login failed",
      );
    }
    setSubmitting(false);
  };

  const handleInstallPwa = async () => {
    // Direct native PWA prompt (no extra confirmation)
    await install();
  };

  const isNavItemActive = (item) => {
    const path = location.pathname || "/";

    if (item.to === "/") {
      return path === "/";
    }

    if (item.to === "/inplay") {
      return path.startsWith("/inplay");
    }

    if ((item.to || "").includes("/casino")) {
      return path.startsWith("/casino");
    }

    // Sports routes: /sports/:id
    if (item.to.startsWith("/sports/") && path.startsWith("/sports/")) {
      const currentId = path.split("/")[2];
      const itemId = item.to.split("/")[2];
      return currentId === itemId;
    }

    return path === item.to;
  };

  const headerNavItems = useMemo(() => {
    const apiList = Array.isArray(sportsInfo) ? sportsInfo : [];
    const apiByName = new Map();
    apiList.forEach((s) => {
      const key = normalizeSportName(s?.name);
      if (key && !apiByName.has(key)) apiByName.set(key, s);
    });

    const staticItems = (MENU_ITEMS || [])
      .filter((it) => it?.label !== "Multi Market")
      .map((it) => {
        const key = normalizeSportName(it?.label);
        const apiSport = key ? apiByName.get(key) : null;

        if (it?.isLink) {
          return {
            id: String(it?.id || it?.label || key),
            to: it?.to || "/",
            label: it?.label || "",
            iconSrc: it?.icon || menuCricket,
            liClassName: "nav-item",
            linkClassName: it?.highlight ? "hightlight-menu" : "",
          };
        }

        const sportId = apiSport?.sport_id ?? getSportIdForMenuLabel(it?.label);
        return {
          id: `sport${sportId ?? it?.id}`,
          to: sportId ? `/sports/${sportId}` : "/",
          label: it?.label || "",
          iconSrc: it?.icon || menuCricket,
          liClassName: "nav-item",
        };
      });

    const staticKeys = new Set(
      (MENU_ITEMS || [])
        .map((it) => normalizeSportName(it?.label))
        .filter(Boolean),
    );

    const extras = apiList
      .filter((s) => {
        const key = normalizeSportName(s?.name);
        return key && !staticKeys.has(key);
      })
      .map((s) => {
        const sportId = String(s?.sport_id || "");
        const isLiveSport = s?.is_live_sport === 1;
        return {
          id: `sport${sportId || s?.name || "extra"}`,
          to: isLiveSport
            ? sportId === "QT"
              ? "/casino?category=QTECH&providerCode=QT&subCategory=ALL"
              : sportId === "GAP"
                ? "/casino?category=GAP&providerCode=GAP&subCategory=ALL"
                : "/casino?category=ALL&providerCode=MAC88&subCategory=Aviator+X"
            : `/sports/${sportId}`,
          label: s?.name || "",
          iconSrc: isLiveSport ? menuCasino : s?.icon || menuCricket,
          liClassName: "nav-item",
          linkClassName: isLiveSport ? "hightlight-menu" : "",
        };
      });

    return [...HEADER_BASE_NAV_ITEMS, ...staticItems, ...extras];
  }, [sportsInfo]);

  const showMobileWalletRow =
    isLoggedIn && (!isFeatureHidden("deposit") || !isFeatureHidden("withdraw"));

  return (
    <>
      <header
        className={`header fixed-top d-flex align-items-center${showMobileWalletRow ? " header--mobile-wallet-nav" : ""}`}
      >
        <div className="container d-flex align-items-center">
          <div className="d-flex align-items-center justify-content-between">
            {logoUrl && (
              <Link
                to="/"
                className={`logo d-flex align-items-center  ${logoUrl ? "bg-black" : ""}`}
              >
                <img alt="" className="img-fluid" src={logoUrl} />
              </Link>
            )}
          </div>
          <div className="livematch">
            <span className="lm_icon">
              <img
                src={iconPopular}
                className="img-fluid"
                style={{ width: "32px" }}
                alt="icon-popular"
              />
            </span>

            <marquee className="lm_datas" aria-live="polite">
              {showCriketFooballTenisInplayMatches.map((match) => (
                <div
                  onClick={() => navigate(`/sports/detail/${match.match_id}`)}
                  key={match.match_id}
                >
                  <b tabIndex="0">{match.name}</b>
                </div>
              ))}
            </marquee>
          </div>
          <div className="search-bar">
            <div ref={searchContainerRef} style={{ position: "relative" }}>
              <form
                noValidate
                onSubmit={onSearchSubmit}
                className="search-form d-flex align-items-center "
              >
                <input
                  type="text"
                  placeholder="Search Events"
                  className="form-control mx-1 mx-md-0"
                  aria-autocomplete="list"
                  value={searchQuery}
                  onChange={onSearchChange}
                  onFocus={() => {
                    if (searchQuery.trim().length >= 3) setSearchOpen(true);
                  }}
                />
                <button type="submit" title="Search">
                  <i className="bi bi-search mx-2"></i>
                </button>
              </form>

              {searchOpen && (
                <div
                  className="search-suggestions z-index-10 mx-2 shadow mx-md-0 border-1"
                  role="listbox"
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    background: "#fff",
                    border: "1px solid #ddd",
                    borderTop: "none",
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                >
                  {searchLoading ? (
                    <div className="text-center py-2 text-muted">
                      Searching…
                    </div>
                  ) : suggestions.length > 0 ? (
                    suggestions.map((m) => {
                      const baseLabel = getLabelForMatch(m) || "—";
                      const timeIso =
                        // m?.match_date ??
                        m?.open_date ?? m?.open_date_format ?? "";
                      const time = formatMatchTime(timeIso);
                      const label = time ? `${baseLabel} (${time})` : baseLabel;
                      const key = `${baseLabel}-${m?.match_id ?? m?.event_id ?? m?.id ?? ""}`;
                      return (
                        <button
                          key={key}
                          type="button"
                          className="w-100 text-start px-3 py-2"
                          style={{
                            border: "none",
                            borderTop: "1px solid #eee",
                            background: "#fff",
                          }}
                          onClick={() => {
                            setSearchOpen(false);
                            setSearchQuery("");
                            navigate(buildMatchRoute(m));
                          }}
                        >
                          <div style={{ fontSize: "0.95rem" }}>{label}</div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="text-center py-2 text-muted">
                      No match found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <Link
            to="#"
            className="rules"
            onClick={() => setShowRulesDialog(true)}
          >
            rules
          </Link>
          <div className="d-none d-lg-flex align-items-center">
            {!isFeatureHidden("deposit") && isLoggedIn && (
              <a
                href="/deposit"
                className="rules btn-deposit ng-star-inserted"
                onClick={(e) => handleWalletRouteNavigation(e, "/deposit")}
              >
                <img src="/deposit-icon.png" alt="menu-deposit" />
                deposit
              </a>
            )}
            {!isFeatureHidden("withdraw") && isLoggedIn && (
              <a
                href="/withdraw"
                className="rules btn-withdraw ng-star-inserted"
                onClick={(e) => handleWalletRouteNavigation(e, "/withdraw")}
              >
                <img src="/withdrawal-icon.png" alt="menu-withdraw" /> withdraw
              </a>
            )}
          </div>

          <nav className="header-nav ms-auto">
            <ul className="d-flex">
              {!isLoggedIn ? (
                <li className="nav-item dropdown pe-3">
                  {!isFeatureHidden("signup") && IS_SIGNUP_SHOW && (
                    <button
                      type="button"
                      className="btn-login"
                      onClick={() => navigate("/signup")}
                    >
                      Signup{" "}
                    </button>
                  )}

                  <button
                    type="button"
                    className="btn-login"
                    onClick={() => setShowLoginDialog(true)}
                  >
                    Login{" "}
                  </button>
                </li>
              ) : (
                <>
                  <li className="nav-item expo_bal ng-star-inserted">
                    <a href="#" className="nav-link">
                      <span>
                        Bal
                        <b>
                          {formatAmount(
                            typeof balance === "object" && balance !== null
                              ? balance.balance
                              : balance,
                          )}
                        </b>
                      </span>
                    </a>
                  </li>
                  <li className="nav-item expo_bal ng-star-inserted">
                    <Link to="/market-analysis" className="nav-link">
                      <i className="bi bi-bar-chart"></i>
                      <span>
                        exp<b>{formatAmount(exposure)}</b>
                      </span>
                    </Link>
                  </li>
                  <li className="nav-item dropdown pe-3">
                    <Dropdown
                      align="end"
                      show={isProfileMenuOpen}
                      onToggle={(nextShow) => setIsProfileMenuOpen(nextShow)}
                    >
                      <Dropdown.Toggle
                        as="button"
                        type="button"
                        className="nav-link nav-profile d-flex align-items-center pe-0 ng-star-inserted btn btn-link p-0 border-0"
                      >
                        <i className="bi bi-person-fill userpro-icons"></i>
                        <span className="ps-lg-2">
                          {userData?.is_demo
                            ? "Demo"
                            : userData?.user_name ||
                              userData?.username ||
                              "User"}
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="dropdown-menu-end dropdown-menu-arrow profile">
                        <div className="dropdown-header">
                          <h6>
                            HI,{" "}
                            {userData?.is_demo
                              ? "Demo"
                              : userData?.user_name ||
                                userData?.username ||
                                "User"}
                          </h6>
                        </div>
                        <li className="balance-information">
                          <div className="balance-row">
                            <div className="balance-text-left">
                              Wallet Amount <small>(Inclusive bonus)</small>
                            </div>
                            <div className="balance-price">
                              {formatAmount(balance?.balance)}
                            </div>
                          </div>
                          <div className="balance-row">
                            <div className="balance-text-left">
                              Net Exposure
                            </div>
                            <div className="balance-price">
                              {formatAmount(exposure)}
                            </div>
                          </div>
                          <div className="balance-row">
                            <div className="balance-text-left">Bonus</div>
                            <div className="balance-price">
                              {formatAmount(bonusAmounts?.earnedBonus)}
                            </div>
                          </div>
                          <div className="balance-row">
                            <div className="balance-text-left">
                              Available Withdrawal
                            </div>
                            <div className="balance-price">0.00</div>
                          </div>
                        </li>

                        {!isDemoUser && (
                          <li className="ab_rae_btns ng-star-inserted">
                            {!isFeatureHidden("bonus") && (
                              <button
                                onClick={() => {
                                  setIsProfileMenuOpen(false);
                                  navigate("/bonus-list");
                                }}
                                className="ab_btns"
                                tabindex="0"
                              >
                                Awaiting Bonus
                              </button>
                            )}
                            {!isFeatureHidden("affiliate") && (
                              <button
                                className="rae_btns"
                                tabindex="0"
                                onClick={() => {
                                  setIsProfileMenuOpen(false);
                                  navigate("/reffer-and-earn");
                                }}
                              >
                                Affiliate REFER AND EARN
                              </button>
                            )}
                          </li>
                        )}
                        <li>
                          <Link
                            to="/"
                            className="dropdown-item d-flex align-items-center deposit-withdraw-sidebar-title"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <img src={homeIcon} alt="menu-home" />
                            <span>home</span>
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/profile"
                            className="dropdown-item d-flex align-items-center"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <i className="bi bi-person"></i>
                            <span>My Profile</span>
                          </Link>
                        </li>

                        {!isDemoUser && !isFeatureHidden("deposit") && (
                          <li className="">
                            <Link
                              to="/deposit"
                              className="dropdown-item d-flex align-items-center deposit-withdraw-sidebar-title deposit"
                              onClick={(e) =>
                                handleWalletRouteNavigation(e, "/deposit", true)
                              }
                            >
                              <img src="/deposit-icon.png" alt="menu-deposit" />
                              <span>Deposit </span>
                            </Link>
                          </li>
                        )}
                        {!isDemoUser && !isFeatureHidden("withdraw") && (
                          <li className="">
                            <Link
                              to="/withdraw"
                              className="dropdown-item d-flex align-items-center deposit-withdraw-sidebar-title withdraw"
                              onClick={(e) =>
                                handleWalletRouteNavigation(
                                  e,
                                  "/withdraw",
                                  true,
                                )
                              }
                            >
                              <img
                                src="/withdrawal-icon.png"
                                alt="menu-withdraw"
                              />
                              <span>Withdraw </span>
                            </Link>
                          </li>
                        )}

                        <li>
                          <Link
                            to="/reports/account-statement"
                            className="dropdown-item d-flex align-items-center"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <i className="bi bi-bar-chart-steps"></i>
                            <span>Account statement</span>
                          </Link>
                        </li>
                        {!isFeatureHidden("transactions") && !isDemoUser && (
                          <li>
                            <Link
                              to="/transactions"
                              className="dropdown-item d-flex align-items-center"
                              onClick={() => setIsProfileMenuOpen(false)}
                            >
                              <i className="bi bi-cash-coin"></i>
                              <span>Transactions</span>
                            </Link>
                          </li>
                        )}
                        {!isDemoUser && (
                          <li>
                            <button
                              className="dropdown-item d-flex align-items-center"
                              onClick={handle2FASettings}
                            >
                              <i className="bi bi-shield-lock"></i>
                              <span>2FA+</span>
                            </button>
                          </li>
                        )}
                        {!isDemoUser && (
                          <li className="ng-star-inserted">
                            <button
                              type="button"
                              className="dropdown-item d-flex align-items-center"
                              onClick={() => {
                                setIsProfileMenuOpen(false);
                                setShowBonusRulesDialog(true);
                              }}
                            >
                              <i className="bi bi-card-text"></i>
                              <span>Bonus Rules</span>
                            </button>
                          </li>
                        )}
                        <li>
                          <Link
                            to="/profile/stake-settings"
                            className="dropdown-item d-flex align-items-center"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <i className="bi bi-bullseye"></i>
                            <span>stake settings</span>
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/reports/profit-loss"
                            className="dropdown-item d-flex align-items-center"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <i className="bi bi-bar-chart-line"></i>
                            <span>profit &amp; loss</span>
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/reports/unsettled-bets"
                            className="dropdown-item d-flex align-items-center"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <i className="bi bi-bar-chart"></i>
                            <span>Unsettled Bets</span>
                          </Link>
                        </li>
                        {!isDemoUser && (
                          <li>
                            <Link
                              to="/profile/change-password"
                              className="dropdown-item d-flex align-items-center"
                              onClick={() => setIsProfileMenuOpen(false)}
                            >
                              <i className="bi bi-lock"></i>
                              <span>change password</span>
                            </Link>
                          </li>
                        )}
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item d-flex align-items-center"
                            onClick={(e) => {
                              e.preventDefault();
                              setIsProfileMenuOpen(false);
                              setIsLoggedIn(false);
                              setUserData(null);
                              clearAuthData();
                              dispatch(logoutAction());
                              // After logout, refresh guest data for sidebar and home
                              dispatch(fetchHomeAllMatches());
                              dispatch(getSportsInfoAsync());
                              navigate("/");
                            }}
                          >
                            <i className="bi bi-box-arrow-right"></i>
                            <span>Sign Out</span>
                          </Link>
                        </li>
                      </Dropdown.Menu>
                    </Dropdown>
                  </li>
                </>
              )}
            </ul>
          </nav>
        </div>
      </header>
      {isLoggedIn &&
        (!isFeatureHidden("deposit") || !isFeatureHidden("withdraw")) && (
          <div className="de_wi_btns">
            {!isFeatureHidden("deposit") && (
              <a
                href="/deposit"
                className="btn-deposit"
                onClick={(e) => handleWalletRouteNavigation(e, "/deposit")}
              >
                <img
                  src="http://reddybook.live/assets/images/deposit-icon.png"
                  alt="menu-deposit"
                />
                deposit
              </a>
            )}
            {!isFeatureHidden("withdraw") && (
              <a
                href="/withdraw"
                className="btn-withdrawal"
                onClick={(e) => handleWalletRouteNavigation(e, "/withdraw")}
              >
                <img
                  src="http://reddybook.live/assets/images/withdrawal-icon.png"
                  alt="menu-withdraw"
                />{" "}
                withdraw
              </a>
            )}
          </div>
        )}
      {showUvMiniCasinoBtn && !location.pathname.includes("/dc/gamev1.1/") && (
        <div className="mini-casino-actions">
          {location.pathname === "/" && (
            <a
              href="#"
              className="exch-mob-nav-link mlobby-main mini-casino-btn"
              onClick={(e) => {
                e.preventDefault();
                mlobbyBtnClick();
              }}
              aria-label="Open games lobby"
            >
              <div className="exch-nav-item-ctn">
                <img className="absolute" src={UVGamesGif} alt="" />
              </div>
            </a>
          )}
          {!!whatsappUrl && (
            <a
              className="mini-whatsapp-btn"
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp support"
            >
              <img src={wpSupportImg} alt="WhatsApp support" />
            </a>
          )}
        </div>
      )}
      <div
        className={`new-middle-menus ${
          isLoggedIn &&
          (!isFeatureHidden("deposit") || !isFeatureHidden("withdraw")) &&
          "new-top"
        }`}
      >
        <ul>
          {headerNavItems.map((item) => {
            const isActive = isNavItemActive(item);
            const linkClasses =
              [item.linkClassName, isActive ? "nmm-active" : ""]
                .filter(Boolean)
                .join(" ") || undefined;

            return (
              <li key={item.id} className={item.liClassName || undefined}>
                <Link
                  id={item.id}
                  to={item.to}
                  className={linkClasses}
                  onClick={(e) => {
                    const to = item.to || "";
                    const isDemo = isDemoUser;
                    if (
                      to.includes("/casino") ||
                      to.includes("/dc/gamev1.1/")
                    ) {
                      if (!isLoggedIn) {
                        e.preventDefault();
                        setShowLoginDialog(true);
                      } else if (isDemo) {
                        e.preventDefault();
                        toast.error(
                          "Demo users are not allowed to play casino games.",
                        );
                      }
                    }
                  }}
                >
                  <img
                    src={item.iconSrc}
                    alt={`menu-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  />{" "}
                  {item.label}{" "}
                </Link>
              </li>
            );
          })}
        </ul>
        <i
          className="bi bi-list-nested toggle-sidebar-btn"
          onClick={toggleSidebar}
        ></i>
      </div>
      <Dialog
        show={showLoginDialog}
        onHide={() => setShowLoginDialog(false)}
        dialogClassName="login-popup"
        centered
      >
        <section className="login-block">
          <button
            type="button"
            aria-label="Close"
            onClick={() => setShowLoginDialog(false)}
            className="btn-close close pull-right"
          >
            <span aria-hidden="true">×</span>
          </button>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6 login-sec">
                <h2 className="text-center">Login Now</h2>
                <form
                  noValidate
                  className="login-form ng-untouched ng-pristine ng-invalid"
                  onSubmit={handleSubmit}
                >
                  <div className="form-group">
                    <label
                      htmlFor="exampleInputEmail1"
                      className="text-uppercase"
                    >
                      Username / Mobile Numbers
                    </label>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="form-control ng-untouched ng-pristine ng-invalid"
                    />
                  </div>
                  <div className="form-group">
                    <label
                      htmlFor="exampleInputPassword1"
                      className="text-uppercase"
                    >
                      Password
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="Enter Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="form-control ng-untouched ng-pristine ng-invalid"
                        aria-describedby="toggle-login-password-visibility"
                        style={{ paddingRight: "44px" }}
                      />
                      <button
                        id="toggle-login-password-visibility"
                        type="button"
                        className="btn"
                        style={{
                          position: "absolute",
                          right: "6px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          padding: "0.25rem 0.5rem",
                          lineHeight: 1,
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        title={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12Z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <path d="M10.733 5.076A10.744 10.744 0 0 1 12 5c7 0 10 7 10 7a18.893 18.893 0 0 1-3.392 4.916" />
                            <path d="M14.084 14.158A3 3 0 0 1 9.84 9.916" />
                            <path d="M17.479 17.499A10.75 10.75 0 0 1 12 19c-7 0-10-7-10-7a18.827 18.827 0 0 1 4.231-5.568" />
                            <path d="M2 2l20 20" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="form-check">
                    <label className="form-check-label">
                      <input type="checkbox" className="form-check-input" />
                      <span>Remember Me?</span>
                    </label>
                  </div>
                  <div className="form-group text-center ng-star-inserted">
                    <a href="#" className="fp">
                      Forgot Password/Username?
                    </a>
                  </div>
                  <button
                    type="submit"
                    className="btn button-login btn-login"
                    disabled={loginLoading}
                  >
                    Log In
                  </button>
                  <button
                    type="button"
                    className="btn button-login btn-login"
                    disabled={loginLoading}
                    onClick={() => handleDemoLogin()}
                  >
                    Login with Demo ID
                  </button>

                  {canInstallPwa && (
                    <div className="form-group text-center">
                      <button
                        type="button"
                        className="btn btn-success w-100 btn-block"
                        onClick={handleInstallPwa}
                      >
                        {langData?.["download_apk"] || "Download APK"}
                        <APKIcon />
                      </button>
                    </div>
                  )}
                </form>

                <div className="copy-text">
                  Powered by <a href="/">{brandNameWithoutSubdomain}</a>
                  {/* <p className="ng-star-inserted">
                    <a href="mailto:reddybook.club@gmail.com">
                      reddybook.club@gmail.com
                    </a>
                  </p> */}
                </div>
              </div>

              <div className="col-md-6 banner-sec d-none d-sm-block">
                <div className="banner-sec-content">
                  <Carousel indicators={false}>
                    <Carousel.Item>
                      <img src={logOne} alt="log-one" className="img-fluid" />
                    </Carousel.Item>
                    <Carousel.Item>
                      <img
                        src={logThree}
                        alt="log-three"
                        className="img-fluid"
                      />
                    </Carousel.Item>
                    <Carousel.Item>
                      <img src={logFour} alt="log-four" className="img-fluid" />
                    </Carousel.Item>
                  </Carousel>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Dialog>
      <Dialog
        show={showRulesDialog}
        onHide={() => setShowRulesDialog(false)}
        centered
        size="lg"
        dialogClassName="rules-popup"
      >
        <div className="card rules-container">
          <div className="card-header">
            <h4 className="mb-0">
              {" "}
              Rules{" "}
              <button
                onClick={() => setShowRulesDialog(false)}
                type="button"
                aria-label="Close"
                className="btn-close close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </h4>
          </div>
          <div className="card-body container-fluid container-fluid-5">
            <Rules />
          </div>
        </div>
      </Dialog>
      <Dialog
        show={showBonusRulesDialog}
        onHide={() => setShowBonusRulesDialog(false)}
        centered
        size="lg"
        dialogClassName="rules-popup"
      >
        <div className="card rules-container">
          <div className="card-header">
            <h4 className="mb-0">
              {" "}
              Bonus Rules{" "}
              <button
                onClick={() => setShowBonusRulesDialog(false)}
                type="button"
                aria-label="Close"
                className="btn-close close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </h4>
          </div>
          <div className="card-body container-fluid container-fluid-5">
            <BonusRules />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default Header;
